export const SCENIC_EVENTS = ['sunrise', 'sunset'];
export const SCENIC_CAPTION_LIMIT = 1024;

const INITIAL_CAMERAS = {
  'bucklers-africa': 'bucklers-africa-main-01',
  'kruger-gate-hotel': 'kruger-gate-hotel',
  'south-post-at-kruger': 'south-post-at-kruger-main-01',
};

export function defaultScenicSettings(client = {}) {
  const name = client.name || client.id || 'our property';
  return {
    cameraId: INITIAL_CAMERAS[client.id] || '',
    sunrise: {
      enabled: false,
      targetType: client.id === 'kruger-gate-hotel' ? 'pattern' : 'preset',
      presetId: client.id === 'bucklers-africa' ? 25 : null,
      patternId: client.id === 'kruger-gate-hotel' ? 2 : null,
      caption: `Good morning from ${name}!\nWishing everyone amazing sightings today.`,
      offsetSeconds: 0,
    },
    sunset: {
      enabled: false,
      targetType: 'preset',
      presetId: null,
      patternId: null,
      caption: `Good evening from ${name}!\nWishing you a peaceful evening in the bush.`,
      offsetSeconds: 0,
    },
  };
}

export function hydrateScenicSettings(client = {}) {
  const defaults = defaultScenicSettings(client);
  const settings = client.scenicCapture;
  if (!settings) return defaults;
  return {
    cameraId: typeof settings.cameraId === 'string' ? settings.cameraId : '',
    ...Object.fromEntries(SCENIC_EVENTS.map((event) => {
      const source = settings[event] || {};
      const presetId = source.presetId === undefined ? defaults[event].presetId : source.presetId;
      const targetType = source.targetType === 'pattern' ? 'pattern' :
        (source.targetType === 'preset' || source.presetId !== undefined ? 'preset' : defaults[event].targetType);
      const patternId = source.patternId === undefined ? defaults[event].patternId : source.patternId;
      return [event, {
        enabled: source.enabled === true,
        targetType,
        presetId: targetType === 'preset' && Number.isInteger(presetId) && presetId > 0 ? presetId : null,
        patternId: targetType === 'pattern' && Number.isInteger(patternId) && patternId > 0 ? patternId : null,
        caption: typeof source.caption === 'string' ? source.caption : defaults[event].caption,
        offsetSeconds: Number.isInteger(source.offsetSeconds) && Math.abs(source.offsetSeconds) <= 3600 ? source.offsetSeconds : 0,
      }];
    })),
  };
}

export function scenicPresetOptions(presets = []) {
  return presets.map((preset) => {
    const raw = preset.backendId ?? preset.backend_id ?? preset.id;
    const value = typeof raw === 'boolean' || raw == null || raw === '' ? NaN : Number(raw);
    return {
      id: preset.id,
      value,
      disabled: preset.enabled === false,
      label: `${preset.name || 'Unnamed view'} · Camera preset ${value} · Entry ${preset.id}${preset.enabled === false ? ' (disabled)' : ''}`,
    };
  }).filter((preset) => Number.isInteger(preset.value) && preset.value > 0)
    .sort((left, right) => left.value - right.value || String(left.id).localeCompare(String(right.id)));
}

// A stale general-settings draft must never restore old scenic settings.
// Read latest in the caller's transaction, then replace only these fields.
export function preserveLatestScenicSettings(payload, latest = {}) {
  const next = { ...payload };
  ['scenicCapture', 'scenicCaptureUpdatedAt'].forEach((field) => {
    delete next[field];
    if (Object.prototype.hasOwnProperty.call(latest, field)) next[field] = latest[field];
  });
  return next;
}

export function scenicRunVideoUrl(run) {
  const value = run.videoUrl || run.mediaUrl || '';
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.href : '';
  } catch (_) {
    return '';
  }
}

export function formatScenicStatus(value) {
  const labels = {
    claimed: 'Scheduled',
    preparing: 'Preparing',
    capturing: 'Recording',
    finalizing: 'Saving video',
    captured: 'Video recorded',
    recorded: 'Video recorded',
    skipped: 'Skipped',
    interrupted: 'Interrupted before recording finished',
    uploaded: 'Uploaded',
    queued: 'Waiting to upload',
    ready: 'Ready',
    failed: 'Failed',
    accepted: 'Submitted to WhatsApp',
    unknown: 'Delivery uncertain — review needed',
    expired: 'Saved without sending: greeting is too late',
    disabled: 'Cancelled: recording disabled',
    no_guests: 'Saved without sending: no guest groups configured',
    partial: 'Some guest groups need review',
  };
  return labels[value] || String(value || '').replace(/[-_]/g, ' ');
}
